import React from "react";
import {
  Button, Card, CardHeader, CardBody, CardTitle,
  Row, Col, Table, UncontrolledTooltip
} from "reactstrap";
import ConfirmationModal from "components/ConfirmationModal/ConfirmationModal";
import { createBackupRequest, listBackupsRequest, deleteBackupRequest, downloadBackupRequest } from "api/backup";

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleString();
};

class Backups extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      backups: null,
      loading: true,
      creating: false,
      deleteKey: '',
      downloadingKey: ''
    };
  }

  async componentDidMount() {
    await this.getBackups();
  }

  getBackups = async () => {
    this.setState({ loading: true });
    try {
      const { backups } = await listBackupsRequest();
      this.setState({ backups, loading: false });
    } catch (e) {
      this.setState({ loading: false });
    }
  };

  createBackup = async () => {
    this.setState({ creating: true });
    try {
      await createBackupRequest();
      await this.getBackups();
    } catch (e) {
      // silently refresh list regardless
      await this.getBackups();
    } finally {
      this.setState({ creating: false });
    }
  };

  openDeleteConfirmation = (key) => () => {
    this.setState({ deleteKey: key });
    this.deleteModal.toggle();
  };

  deleteBackup = async () => {
    const { deleteKey } = this.state;
    await deleteBackupRequest(deleteKey);
    await this.getBackups();
  };

  downloadBackup = (key) => async () => {
    this.setState({ downloadingKey: key });
    try {
      const blob = await downloadBackupRequest(key);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = key.split('/').pop();
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      this.setState({ downloadingKey: '' });
    }
  };

  render() {
    const { backups, loading, creating, downloadingKey } = this.state;
    return (
      <>
        <div className="content">
          <Row>
            <Col md="12">
              <Card>
                <CardHeader>
                  <CardTitle className="d-inline-block" tag="h4">Backups</CardTitle>
                  <Button
                    className="pull-right mb-3 add-btn"
                    color="primary"
                    onClick={this.createBackup}
                    disabled={creating}
                  >
                    {creating ? 'Creating...' : <><i className="tim-icons icon-upload" /> Create Backup</>}
                  </Button>
                </CardHeader>
                <CardBody>
                  <Table responsive>
                    <thead className="text-primary">
                      <tr>
                        <th>File</th>
                        <th>Size</th>
                        <th>Date</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={4}>Loading...</td></tr>
                      ) : backups && backups.length ? (
                        backups.map((backup) => {
                          const filename = backup.key.split('/').pop();
                          const isDownloading = downloadingKey === backup.key;
                          const safeId = filename.replace(/[^a-zA-Z0-9]/g, '-');
                          return (
                            <tr key={backup.key}>
                              <td>{filename}</td>
                              <td>{formatSize(backup.size)}</td>
                              <td>{formatDate(backup.lastModified)}</td>
                              <td className="text-right">
                                <Button
                                  className="btn-link btn-icon"
                                  color="success"
                                  size="sm"
                                  id={`tooltip-download-${safeId}`}
                                  onClick={this.downloadBackup(backup.key)}
                                  disabled={isDownloading}
                                >
                                  <i className={isDownloading ? "tim-icons icon-refresh-02" : "tim-icons icon-cloud-download-93"} />
                                </Button>
                                <UncontrolledTooltip delay={0} target={`tooltip-download-${safeId}`}>
                                  Download
                                </UncontrolledTooltip>
                                <Button
                                  className="btn-link btn-icon"
                                  color="danger"
                                  size="sm"
                                  id={`tooltip-delete-${safeId}`}
                                  onClick={this.openDeleteConfirmation(backup.key)}
                                >
                                  <i className="tim-icons icon-simple-remove" />
                                </Button>
                                <UncontrolledTooltip delay={0} target={`tooltip-delete-${safeId}`}>
                                  Delete
                                </UncontrolledTooltip>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr><td colSpan={4}>No backups found. Create one to get started.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <ConfirmationModal
            ref={el => this.deleteModal = el}
            title="Delete Backup?"
            buttons={[
              { title: 'Cancel', className: 'btn-primary-override btn btn-primary' },
              { title: 'Delete', className: 'btn-danger-override btn btn-danger', onClick: this.deleteBackup }
            ]}
          >
            Are you sure you want to delete this backup? This cannot be undone.
          </ConfirmationModal>
        </div>
      </>
    );
  }
}

export default Backups;
